import React, { useState, useRef, useEffect } from "react";
import { Dropdown, Button } from "react-bootstrap";
import moment from "moment";
import "./DatepickerRangeAirbnb.scss";

const DatepickerRangeAirbnb = ({ placeholder = "YYYY-MM-DD - YYYY-MM-DD", onChange, startDate, endDate }) => {
    const [show, setShow] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(startDate ? moment(startDate) : null);
    const [tempEndDate, setTempEndDate] = useState(endDate ? moment(endDate) : null);
    const [hoverDate, setHoverDate] = useState(null);
    const [dropdownAlign, setDropdownAlign] = useState('start');
    
    // Track the left calendar's month (right will always be +1 month)
    const [leftMonth, setLeftMonth] = useState(
        startDate ? moment(startDate).startOf('month') : moment().startOf('month')
    );
    
    const predefinedRanges = {
        '오늘': [moment(), moment()],
        '어제': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
        '지난 7일': [moment().subtract(6, 'days'), moment()],
        '지난 30일': [moment().subtract(29, 'days'), moment()],
        '이번 달': [moment().startOf('month'), moment().endOf('month')],
        '지난 달': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
        '전체': null,
    };

    const handleRangeClick = (rangeName) => {
        const range = predefinedRanges[rangeName];
        if (rangeName === '전체') {
            setTempStartDate(null);
            setTempEndDate(null);
            onChange(["", ""]);
        } else {
            const [start, end] = range;
            setTempStartDate(start);
            setTempEndDate(end);
            onChange([start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")]);
        }
        // Don't change the calendar view
    };

    const handleDateClick = (date) => {
        if (!tempStartDate || (tempStartDate && tempEndDate)) {
            // Start a new selection
            setTempStartDate(date);
            setTempEndDate(null);
        } else {
            // Complete the selection
            if (date.isBefore(tempStartDate)) {
                setTempEndDate(tempStartDate);
                setTempStartDate(date);
            } else {
                setTempEndDate(date);
            }
        }
    };

    const handleApply = () => {
        if (tempStartDate && tempEndDate) {
            onChange([tempStartDate.format("YYYY-MM-DD"), tempEndDate.format("YYYY-MM-DD")]);
        }
        setShow(false);
    };

    const handleCancel = () => {
        setTempStartDate(startDate ? moment(startDate) : null);
        setTempEndDate(endDate ? moment(endDate) : null);
        setShow(false);
    };

    const navigateMonth = (direction) => {
        setLeftMonth(prev => prev.clone().add(direction, 'month'));
    };

    const isDateInRange = (date) => {
        if (!tempStartDate) return false;
        
        if (tempEndDate) {
            return date.isSameOrAfter(tempStartDate, 'day') && date.isSameOrBefore(tempEndDate, 'day');
        } else if (hoverDate && tempStartDate) {
            const start = tempStartDate;
            const end = hoverDate.isBefore(start) ? start : hoverDate;
            const actualStart = hoverDate.isBefore(start) ? hoverDate : start;
            return date.isSameOrAfter(actualStart, 'day') && date.isSameOrBefore(end, 'day');
        }
        
        return date.isSame(tempStartDate, 'day');
    };

    const isStartDate = (date) => {
        return tempStartDate && date.isSame(tempStartDate, 'day');
    };

    const isEndDate = (date) => {
        return tempEndDate && date.isSame(tempEndDate, 'day');
    };

    const renderCalendar = (monthMoment) => {
        const startOfMonth = monthMoment.clone().startOf('month');
        const endOfMonth = monthMoment.clone().endOf('month');
        const startDate = startOfMonth.clone().startOf('week');
        const endDate = endOfMonth.clone().endOf('week');
        
        const calendar = [];
        const current = startDate.clone();
        
        // Month and year header
        calendar.push(
            <div key="header" className="calendar-header">
                <div className="month-year">
                    {monthMoment.format('YYYY년 M월')}
                </div>
            </div>
        );
        
        // Day headers
        calendar.push(
            <div key="weekdays" className="weekdays">
                {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                    <div key={day} className="weekday">{day}</div>
                ))}
            </div>
        );
        
        // Days
        const weeks = [];
        while (current.isSameOrBefore(endDate)) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                const date = current.clone();
                const isCurrentMonth = date.month() === monthMoment.month();
                const isToday = date.isSame(moment(), 'day');
                const isInRange = isDateInRange(date);
                const isStart = isStartDate(date);
                const isEnd = isEndDate(date);
                // Remove past date restriction - allow selecting past dates for reports
                
                week.push(
                    <div
                        key={date.format('YYYY-MM-DD')}
                        className={`
                            day
                            ${!isCurrentMonth ? 'other-month' : ''}
                            ${isToday ? 'today' : ''}
                            ${isInRange ? 'in-range' : ''}
                            ${isStart ? 'start-date' : ''}
                            ${isEnd ? 'end-date' : ''}
                        `}
                        onClick={() => handleDateClick(date)}
                        onMouseEnter={() => setHoverDate(date)}
                        onMouseLeave={() => setHoverDate(null)}
                    >
                        <span className="day-number">{date.date()}</span>
                    </div>
                );
                current.add(1, 'day');
            }
            weeks.push(
                <div key={current.format('W')} className="week">
                    {week}
                </div>
            );
        }
        
        calendar.push(
            <div key="weeks" className="weeks">
                {weeks}
            </div>
        );
        
        return calendar;
    };

    return (
        <Dropdown show={show} onToggle={setShow} className="date-range-picker-airbnb">
            <Dropdown.Toggle 
                as="div"
                className="form-control cursor-pointer d-flex justify-content-between align-items-center"
                style={{ minWidth: "210px" }}
                onClick={(e) => {
                    // Check if dropdown would go beyond right edge of viewport
                    const rect = e.target.getBoundingClientRect();
                    const dropdownWidth = 750; // from SCSS min-width
                    const viewportWidth = window.innerWidth;
                    
                    if (rect.left + dropdownWidth > viewportWidth) {
                        setDropdownAlign('end');
                    } else {
                        setDropdownAlign('start');
                    }
                    
                    setShow(!show);
                }}
            >
                <span>
                    {tempStartDate && tempEndDate 
                        ? `${tempStartDate.format('YYYY-MM-DD')} - ${tempEndDate.format('YYYY-MM-DD')}`
                        : placeholder}
                </span>
                <i className="bi bi-calendar3"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu 
                className={`date-range-dropdown ${dropdownAlign === 'end' ? 'dropdown-menu-end' : ''}`}
                align={dropdownAlign}
            >
                <div className="d-flex">
                    {/* Predefined Ranges */}
                    <div className="predefined-ranges">
                        <div className="ranges-title">빠른 선택</div>
                        {Object.keys(predefinedRanges).map((rangeName) => (
                            <Button
                                key={rangeName}
                                variant="link"
                                className="range-item"
                                onClick={() => handleRangeClick(rangeName)}
                            >
                                {rangeName}
                            </Button>
                        ))}
                    </div>

                    {/* Calendar Section */}
                    <div className="calendar-section">
                        {/* Navigation */}
                        <div className="calendar-navigation">
                            <Button 
                                variant="link" 
                                className="nav-btn"
                                onClick={() => navigateMonth(-1)}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </Button>
                            <div className="calendars-wrapper">
                                <div className="calendar-container">
                                    {renderCalendar(leftMonth)}
                                </div>
                                <div className="calendar-container">
                                    {renderCalendar(leftMonth.clone().add(1, 'month'))}
                                </div>
                            </div>
                            <Button 
                                variant="link" 
                                className="nav-btn"
                                onClick={() => navigateMonth(1)}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </Button>
                        </div>

                        {/* Actions */}
                        <div className="actions">
                            <Button variant="link" size="sm" onClick={handleCancel}>
                                취소
                            </Button>
                            <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={handleApply}
                                disabled={!tempStartDate || !tempEndDate}
                            >
                                확인
                            </Button>
                        </div>
                    </div>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default DatepickerRangeAirbnb;